import { useTranslation } from 'react-i18next';
import LocalizedText from '@components/LocalizedText/LocalizedText';
import Header from '@components/Header/TransHeader';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlassStyles } from '@hooks/useGlassStyles';
import { useLanguageNormalizer } from '@hooks/useLanguageNormalizer';
import { SEO, generatePageTitle, generateMetaDescription, BASE_KEYWORDS } from '@components/SEO';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/ui/glassdialog';
import { getBanners } from '../../services/bannerService';
import type { Banner } from '../../types/banner';
import { getParentCategories } from '../../services/categoryService';
import type { Category } from '../../services/categoryService';
import Footer from '@components/Footer/Footer';
import getResponsiveImageUrl from '../../helper/getResponsiveImageUrl';
import LazyOptimizedImage from '@components/OptimizedImage/LazyOptimizedImage';
import OptimizedImageWithFallback from '@components/OptimizedImage/OptimizedImageWithFallback';
import { preloadFirstBanner } from '../../utils/resourcePreloader';
import { logger } from '../../utils/logger';
import { generateCategorySlug } from '../../utils/seoUtils';
import { getContrastColor } from '../../styles/glass-effects';

function MyComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { normalizeCategories } = useLanguageNormalizer();
  const [currentSlide, setCurrentSlide] = useState(0);
  // Initialize with default color
  const [slides, setSlides] = useState<Banner[]>([]);
  const [dominantColors, setDominantColors] = useState<string[]>(['rgb(0, 65, 122)']);
  const [showMoreItems, setShowMoreItems] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [normalizedCategories, setNormalizedCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [isSliderPaused, setIsSliderPaused] = useState(false);
  // const [isHovering, setIsHovering] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  // const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Parallel fetch for better performance
  useEffect(() => {
    const fetchInitialData = async () => {
      let hasBanners = false;

      try {
        setCategoriesLoading(true);

        // Fetch banners and categories in parallel to reduce critical path latency
        const [banners, fetchedCategories] = await Promise.all([
          getBanners(),
          getParentCategories(i18n.language),
        ]);

        // Handle banners
        if (banners.length > 0) {
          hasBanners = true;
          setSlides(banners);
          // Initialize colors array with default color
          setDominantColors(new Array(banners.length).fill('rgb(0, 65, 122)'));

          // Preload the first banner image for LCP optimization
          if (banners[0]?.imageUrl) {
            const firstBannerUrl = getResponsiveImageUrl(banners[0]);
            if (firstBannerUrl) {
              preloadFirstBanner(firstBannerUrl);
            }
          }
        }

        // Handle categories
        setCategories(fetchedCategories);
      } catch (error) {
        logger.error('Error fetching initial data:', error);
      } finally {
        setCategoriesLoading(false);

        // Avoid trapping the homepage in a permanent loading state when banner requests fail.
        if (!hasBanners) {
          setIsLoading(false);
        }
      }
    };

    fetchInitialData();
  }, [i18n.language]);

  // Normalize categories whenever language changes or categories are updated
  useEffect(() => {
    if (categories.length > 0) {
      logger.log('Normalizing categories for language:', i18n.language);
      logger.log(
        'Raw categories before normalization:',
        categories.map((c) => ({
          id: c._id,
          name: c.name,
          _nameMultilingual: c._nameMultilingual,
        })),
      );
      const normalized = normalizeCategories(categories, i18n.language);
      logger.log(
        'Normalized categories after normalization:',
        normalized.map((c) => ({
          id: c._id,
          name: c.name,
        })),
      );
      setNormalizedCategories(normalized);
      logger.log('State updated with normalized categories');
    }
  }, [i18n.language, categories, categories.length, normalizeCategories]);

  // Debug: Log when normalizedCategories state changes
  useEffect(() => {
    logger.log(
      'normalizedCategories state changed:',
      normalizedCategories.map((c) => ({
        id: c._id,
        name: c.name,
      })),
    );
  }, [normalizedCategories, categories.length]);

  // Get dominant color from image with optimized ColorThief
  const getDominantColor = async (imageUrl: string): Promise<string> => {
    const cacheKey = `${imageUrl}_color_v2`;
    const cachedColor = sessionStorage.getItem(cacheKey);

    if (cachedColor) {
      return cachedColor;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const sampleWidth = 72;
          const sampleHeight = 20;
          canvas.width = sampleWidth;
          canvas.height = sampleHeight;

          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) {
            throw new Error('Canvas context not available');
          }

          const sourceWidth = img.naturalWidth || img.width;
          const sourceHeight = img.naturalHeight || img.height;
          const headerAreaHeight = Math.max(1, Math.floor(sourceHeight * 0.22));
          const centerX = Math.floor(sourceWidth * 0.2);
          const centerWidth = Math.max(1, Math.floor(sourceWidth * 0.6));

          // Sample only the top-center area where the navbar overlays the banner.
          ctx.drawImage(
            img,
            centerX,
            0,
            centerWidth,
            headerAreaHeight,
            0,
            0,
            sampleWidth,
            sampleHeight,
          );

          const { data } = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
          let rTotal = 0;
          let gTotal = 0;
          let bTotal = 0;
          let pixelCount = 0;

          for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            if (alpha < 16) continue;
            rTotal += data[i];
            gTotal += data[i + 1];
            bTotal += data[i + 2];
            pixelCount += 1;
          }

          if (pixelCount > 0) {
            const r = Math.round(rTotal / pixelCount);
            const g = Math.round(gTotal / pixelCount);
            const b = Math.round(bTotal / pixelCount);
            const color = `rgb(${r}, ${g}, ${b})`;
            sessionStorage.setItem(cacheKey, color);
            resolve(color);
            return;
          }

          throw new Error('No valid pixels from header sample');
        } catch (sampleError) {
          logger.warn('Header color sampling failed, falling back to ColorThief:', sampleError);

          // Fallback to ColorThief when canvas sampling fails (e.g. CORS-tainted images)
          import('../../utils/tbtOptimization')
            .then(({ deferredImports }) => {
              deferredImports
                .colorThief()
                .then((module) => {
                  try {
                    const colorThiefModule = module as typeof import('colorthief');
                    const colorThief = new colorThiefModule.default();

                    const colorResult = colorThief.getColor(img);
                    if (colorResult && Array.isArray(colorResult) && colorResult.length >= 3) {
                      const [r, g, b] = colorResult;
                      const color = `rgb(${r}, ${g}, ${b})`;
                      sessionStorage.setItem(cacheKey, color);
                      resolve(color);
                    } else {
                      logger.warn('ColorThief returned invalid color data:', colorResult);
                      resolve('rgb(0, 65, 122)');
                    }
                  } catch (error) {
                    logger.warn('Error extracting color with ColorThief fallback:', error);
                    resolve('rgb(0, 65, 122)');
                  }
                })
                .catch(() => {
                  resolve('rgb(0, 65, 122)');
                });
            })
            .catch(() => {
              resolve('rgb(0, 65, 122)');
            });
        }
      };

      img.onerror = () => {
        logger.warn('Error loading image:', imageUrl);
        resolve('rgb(0, 65, 122)');
      };

      img.src = imageUrl;
    });
  };

  const currentColor = dominantColors[currentSlide] || 'rgb(0, 65, 122)';
  const hasSlides = slides.length > 0;
  const contrastText = getContrastColor(currentColor);
  const carouselControlColor = contrastText === 'white' ? '#ffffff' : '#111827';

  const { glassStyle } = useGlassStyles(currentColor);

  // Extract colors when slides change (individual processing)
  useEffect(() => {
    if (slides.length === 0) return;

    let isCancelled = false;
    let idleId: number | undefined;

    const extractSlideColors = async () => {
      setDominantColors(new Array(slides.length).fill('rgb(0, 65, 122)'));

      for (let index = 0; index < slides.length; index += 1) {
        if (isCancelled) return;

        try {
          const color = await getDominantColor(slides[index].imageUrl);

          if (isCancelled) return;

          setDominantColors((prevColors) => {
            const newColors = [...prevColors];
            newColors[index] = color;
            return newColors;
          });
        } catch (error) {
          logger.warn(`Error getting color for slide ${index}:`, error);
        }
      }
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback?.(
        () => {
          void extractSlideColors();
        },
        { timeout: 1500 },
      );
    } else {
      void extractSlideColors();
    }

    return () => {
      isCancelled = true;

      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback?.(idleId);
      }
    };
  }, [slides]);

  const nextSlide = useCallback(() => {
    if (slides.length > 0) {
      // Check if there are slides
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    if (slides.length > 0) {
      // Check if there are slides
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  }, [slides.length]);

  const handleNextSlide = useCallback(() => {
    setIsSliderPaused(true);
    nextSlide();
    // Resume auto-slide after 8 seconds
    setTimeout(() => setIsSliderPaused(false), 8000);
  }, [nextSlide]);

  const handlePrevSlide = useCallback(() => {
    setIsSliderPaused(true);
    prevSlide();
    // Resume auto-slide after 8 seconds
    setTimeout(() => setIsSliderPaused(false), 8000);
  }, [prevSlide]);

  // Touch handlers for mobile swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextSlide();
    } else if (isRightSwipe) {
      handlePrevSlide();
    }
  }, [touchStart, touchEnd, handleNextSlide, handlePrevSlide]);

  // Improved hover handlers
  const handleMouseEnter = useCallback(() => {
    // setIsHovering(true);
    setIsSliderPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // setIsHovering(false);
    // Add 5 second delay before resuming auto-slide
    setTimeout(() => {
      setIsSliderPaused(false);
    }, 5000);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevSlide();
      } else if (event.key === 'ArrowRight') {
        handleNextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNextSlide, handlePrevSlide]);

  // Progress tracking
  useEffect(() => {
    if (slides.length > 0 && !isSliderPaused) {
      // setProgress(0);
      // const interval = setInterval(() => {
      //   setProgress(prev => {
      //     if (prev >= 100) {
      //       return 0;
      //     }
      //     return prev + 2; // 2% every 100ms = 100% in 5 seconds
      //   });
      // }, 100);

      const timer = setTimeout(() => {
        nextSlide();
      }, 5000); // 5 seconds

      return () => {
        clearTimeout(timer);
        // clearInterval(interval);
      };
    }
  }, [slides.length, nextSlide, isSliderPaused, currentSlide]);

  // Loading state management - optimize for LCP
  useEffect(() => {
    if (slides.length > 0) {
      // Set loading to false immediately when first banner is available
      setIsLoading(false);
    }
  }, [slides.length]);

  // Preload first banner for immediate LCP improvement
  useEffect(() => {
    if (slides.length > 0 && slides[0]?.imageUrl) {
      const firstBannerUrl = getResponsiveImageUrl(slides[0]);
      if (firstBannerUrl) {
        const img = new Image();
        img.onload = () => {
          // Banner is loaded, ensure loading state is false
          setIsLoading(false);
        };
        img.src = firstBannerUrl;
      }
    }
  }, [slides]);

  // State for dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState<number | null>(null);

  // Modify handleSlideClick function
  const handleSlideClick = (index: number) => {
    setSelectedSlide(index);
    setIsDialogOpen(true);
  };

  // Handle category click to navigate to product page
  const handleCategoryClick = (category: Category) => {
    // Use generateCategorySlug to handle Myanmar text properly
    const categorySlug = generateCategorySlug(category.name);
    navigate(`/product/${categorySlug}`);
  };

  // const changeLanguage = (lng: string) => {
  //   i18n.changeLanguage(lng);
  // };

  return (
    <>
      <SEO
        title={generatePageTitle('Home')}
        description={generateMetaDescription(
          'EZTopup - Your trusted partner for mobile top-up, data packages, and e-gift cards in Myanmar. Fast, secure, and convenient digital recharge services.',
        )}
        keywords={[...BASE_KEYWORDS, 'home', 'main page', 'digital services'].join(', ')}
        url="https://eztopup.com.mm/"
      />
      <div className="w-full h-auto md:min-h-screen md:max-h-screen bg-white flex flex-col">
        <Header
          currentSlide={currentSlide}
          headerColors={hasSlides ? dominantColors : ['rgb(255, 255, 255)']}
        />

        {/* Hero Carousel Section */}
        <div
          className="relative h-[450px] overflow-hidden carousel-container select-none group"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <div className="banner-skeleton w-full h-full"></div>
            </div>
          ) : !hasSlides ? (
            <div className="w-full h-full bg-gray-100" aria-hidden="true" />
          ) : (
            <div
              className="flex transition-transform duration-500 ease-in-out h-full"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div
                  key={slide._id}
                  className="w-full h-full flex-shrink-0 cursor-pointer relative overflow-hidden hover:scale-105 transition-transform duration-1000"
                  onClick={() => handleSlideClick(index)}
                >
                  <LazyOptimizedImage
                    src={getResponsiveImageUrl(slide) || ''}
                    alt={slide.title || `Banner ${index + 1}`}
                    className="w-full h-full object-cover"
                    size="large"
                    lazy={index !== 0}
                    priority={index === 0}
                    quality={90}
                    responsive={true}
                    useBunnyOptimization={true}
                    bunnyPreset="hero"
                    bunnyPresetSize={1920}
                    responsiveWidths={[480, 768, 1024, 1280, 1920]}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Navigation Buttons */}
          {hasSlides && (
            <>
              <button
                onClick={handlePrevSlide}
                aria-label="Previous slide"
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full p-3 transition-all duration-300 hover:shadow-xl active:scale-95 z-10 opacity-0 group-hover:opacity-100"
                style={{ ...glassStyle, color: carouselControlColor }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <button
                onClick={handleNextSlide}
                aria-label="Next slide"
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full p-3 transition-all duration-300 hover:shadow-xl active:scale-95 z-10 opacity-0 group-hover:opacity-100"
                style={{ ...glassStyle, color: carouselControlColor }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Progress bar */}
          {/* {!isSliderPaused && slides.length > 1 && (
            <div className="absolute top-0 left-0 w-full h-1 bg-white/20">
              <div
                className="h-full bg-white transition-all duration-100 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          )} */}

          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
              <div className="text-gray-500">Loading...</div>
            </div>
          )}

          {/* Slide indicators */}
          {hasSlides && slides.length > 1 && !isLoading && (
            <div className="absolute bottom-6 right-6 hidden lg:flex gap-1 px-3 py-2 rounded-full bg-black/20 backdrop-blur-md">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentSlide(index);
                    setIsSliderPaused(true);
                    setTimeout(() => setIsSliderPaused(false), 3000);
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                  className="p-1.5 rounded-full transition-all duration-300 hover:bg-white/10 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
                >
                  <div
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentSlide === index ? 'bg-white w-6' : 'bg-white/50'
                    }`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navigation Grid */}
        <div className="relative z-10 max-w-2xl mx-auto w-[90%] -mt-18">
          <div className="grid grid-cols-4 gap-4 md:gap-6 p-4 md:p-6 rounded-[34px] shadow-[0_4px_30px_rgba(0,0,0,0.1)] bg-white/21 backdrop-blur-[14px] border border-white/27">
            {categoriesLoading ? (
              // Loading state
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-200/80 rounded-2xl flex items-center justify-center mb-2 md:mb-3 animate-pulse">
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-gray-300 rounded"></div>
                  </div>
                  <div className="w-16 h-3 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ))
            ) : (
              <>
                {/* Show first 4 items if total items <= 4, otherwise show first 3 items + more button */}
                {normalizedCategories.length <= 4 ? (
                  // Show all items if 4 or fewer
                  normalizedCategories.map((category) => (
                    <div
                      key={category._id}
                      className="flex flex-col items-center group cursor-pointer"
                      onClick={() => handleCategoryClick(category)}
                    >
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100/80 rounded-2xl flex items-center justify-center mb-2 md:mb-3 shadow-sm transition-all duration-300 hover:shadow-lg active:scale-95">
                        {category.imageUrl || category.icon ? (
                          <OptimizedImageWithFallback
                            src={category.imageUrl || category.icon || ''}
                            alt={category.name || 'Category'}
                            className="w-6 h-6 md:w-8 md:h-8"
                            useBunnyOptimization={true}
                            bunnyPreset="square"
                            bunnyPresetSize={64}
                          />
                        ) : (
                          <svg
                            className="w-6 h-6 md:w-8 md:h-8 text-gray-600 group-hover:text-blue-600"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 18h-8a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v4M15 10v12M10 15h10"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </div>
                      <p className="text-sm font-sm text-gray-700 group-hover:text-blue-700 text-center">
                        {category.name}
                      </p>
                    </div>
                  ))
                ) : (
                  <>
                    {/* Show first 3 items */}
                    {normalizedCategories.slice(0, 3).map((category) => (
                      <div
                        key={category._id}
                        className="flex flex-col items-center group cursor-pointer"
                        onClick={() => handleCategoryClick(category)}
                      >
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100/80 rounded-2xl flex items-center justify-center mb-2 md:mb-3 shadow-sm transition-all duration-300 hover:shadow-lg active:scale-95">
                          {category.imageUrl || category.icon ? (
                            <OptimizedImageWithFallback
                              src={category.imageUrl || category.icon || ''}
                              alt={category.name || 'Category'}
                              className="w-6 h-6 md:w-8 md:h-8"
                              useBunnyOptimization={true}
                              bunnyPreset="thumbnail"
                              bunnyPresetSize={64}
                            />
                          ) : (
                            <svg
                              className="w-6 h-6 md:w-8 md:h-8 text-gray-600 group-hover:text-blue-600"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 18h-8a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v4M15 10v12M10 15h10"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                        </div>
                        <p className="text-sm font-sm text-gray-700 group-hover:text-blue-700 text-center">
                          {category.name}
                        </p>
                      </div>
                    ))}

                    {/* More Options Button - Only show if items > 4 */}
                    {normalizedCategories.length > 4 && (
                      <div className="relative">
                        <div
                          className="flex flex-col items-center group cursor-pointer"
                          onClick={() => setShowMoreItems(!showMoreItems)}
                        >
                          <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100/80 rounded-2xl flex items-center justify-center mb-2 md:mb-3 shadow-sm transition-all duration-300 hover:shadow-lg active:scale-95">
                            <div className="grid grid-cols-3 grid-rows-3 gap-[2px] p-[2px]">
                              {normalizedCategories.slice(3, 12).map((category, index) => {
                                // If more than 9 items and this is the last spot, show "+" instead
                                if (index === 8 && normalizedCategories.length > 12) {
                                  return (
                                    <div
                                      key="more"
                                      className="w-2 h-2 md:w-3 md:h-3 flex items-center justify-center"
                                    >
                                      <span className="text-[8px] md:text-[10px] text-gray-600 font-bold">
                                        +
                                      </span>
                                    </div>
                                  );
                                }

                                // Regular mini icon
                                return (
                                  <div
                                    key={category._id}
                                    className="w-2 h-2 md:w-3 md:h-3 bg-gray-400/60 rounded-[3px] flex items-center justify-center"
                                  >
                                    {category.imageUrl || category.icon ? (
                                      <OptimizedImageWithFallback
                                        src={category.imageUrl || category.icon || ''}
                                        alt={category.name || 'Category'}
                                        className="w-1.5 h-1.5 md:w-2 md:h-2"
                                        useBunnyOptimization={true}
                                        bunnyPreset="thumbnail"
                                        bunnyPresetSize={16}
                                      />
                                    ) : (
                                      <svg
                                        className="w-1.5 h-1.5 md:w-2 md:h-2 text-gray-600"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M12 18h-8a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v4M15 10v12M10 15h10"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <LocalizedText
                            as="p"
                            fontSize="14px"
                            mmFontSize="12.5px"
                            className="font-medium text-gray-700 group-hover:text-blue-700 text-center"
                          >
                            +{normalizedCategories.length - 3} {t('more')}
                          </LocalizedText>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Dropdown Menu */}
        {showMoreItems && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-all duration-300 ease-in-out"
              onClick={() => setShowMoreItems(false)}
            />

            {/* Menu Modal - Center on all screen sizes */}
            <div
              className={`fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-lg mx-auto z-50 transition-all duration-300 ease-out
                            }`}
            >
              <div className="rounded-4xl shadow-lg overflow-hidden" style={{ ...glassStyle }}>
                {/* Menu Items Grid */}
                <div className="bg-white/60 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[14px]">
                  <div className="p-4">
                    <div className="grid grid-cols-3 gride-rows-3 gap-3">
                      {normalizedCategories.slice(3).map((category) => (
                        <div
                          key={category._id}
                          className="flex flex-col items-center p-2 rounded-xl cursor-pointer group transition-all duration-200"
                          onClick={() => handleCategoryClick(category)}
                        >
                          <div className="w-12 h-12 mb-2 bg-gray-100/80 rounded-xl flex items-center justify-center shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                            {category.imageUrl || category.icon ? (
                              <OptimizedImageWithFallback
                                src={category.imageUrl || category.icon || ''}
                                alt={category.name || 'Category'}
                                className="w-6 h-6"
                                useBunnyOptimization={true}
                                bunnyPresetSize={48}
                              />
                            ) : (
                              <svg
                                className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors duration-200"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 18h-8a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v4M15 10v12M10 15h10"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="text-xs font-medium text-gray-700 group-hover:text-blue-700 text-center">
                            {category.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Enhanced Banner Visit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent
            dominantColor={selectedSlide !== null ? dominantColors[selectedSlide] : currentColor}
            className="max-w-lg"
            primaryButton={{
              label: `${t('visit_link') || 'Visit Link'}`,
              onClick: () => {
                if (selectedSlide !== null) {
                  window.open(slides[selectedSlide].linkUrl, '_blank', 'noopener,noreferrer');
                  setIsDialogOpen(false);
                }
              },
            }}
            // secondaryButton={{
            //   label: `${t('cancel') || 'Cancel'}`,
            //   onClick: () => setIsDialogOpen(false),
            // }}
          >
            <div className="text-center space-y-4">
              {/* Icon */}
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#056cf2] to-[#04d9b2] rounded-full flex items-center justify-center shadow-lg">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>

              {/* Title */}
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-center">
                  {t('visit_external_link') || 'Visit External Link'}
                </DialogTitle>
                <DialogDescription className="text-center text-base mt-2">
                  {selectedSlide !== null && (
                    <div className="space-y-3">
                      <p className="font-medium text-lg">{slides[selectedSlide].title}</p>
                      <p className="text-sm leading-6">{slides[selectedSlide].description}</p>
                    </div>
                  )}
                </DialogDescription>
              </DialogHeader>

              {/* Banner Preview */}
              {/* <div className="mt-6">
                {selectedSlide !== null && (
                  <div className="relative overflow-hidden rounded-xl shadow-lg">
                    <ImageWithFallback
                      src={slides[selectedSlide].imageUrl || ''}
                      alt={slides[selectedSlide].title || 'Banner'}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                )}
              </div> */}

              {/* Security Notice */}
              {/* <div className="bg-amber-50/80 border border-amber-200/80 rounded-lg p-4 mt-6">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <div className="text-left">
                    <p className="text-sm font-medium text-amber-800">
                      {t('external_link_warning') || 'External Link Notice'}
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      {t('external_link_description') ||
                        'You are about to visit an external website. Please ensure you trust this link before proceeding.'}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* URL Display */}
              {/* {selectedSlide !== null && slides[selectedSlide].linkUrl && (
                <div className="bg-gray-50 rounded-lg p-3 mt-4">
                  <p className="text-xs text-gray-500 mb-1">{t('destination') || 'Destination'}:</p>
                  <p className="text-sm font-mono text-gray-700 break-all">
                    {slides[selectedSlide].linkUrl}
                  </p>
                </div>
              )} */}
            </div>
          </DialogContent>
        </Dialog>
        <div className="mt-16 pb-24 md:pb-16">
          <Footer />
        </div>
      </div>
    </>
  );
}

export default MyComponent;
